/*
 * Copyright (c) 2025 curryfirm.com
 */

import {
    Box,
    Typography,
    Radio,
    RadioGroup,
    FormControlLabel,
    FormControl,
    FormLabel,
    Button,
} from '@mui/material';
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    selectReturnToSummaryAvailable,
    selectStoredRouteType,
    setReturnToSummaryAvailable,
    setStoredRouteType,
    setStep,
    WIZARD_STEPS,
} from '../../../features/workflow/workflowSlice.js';
import { ROUTE_TYPES } from '../../../constants.js';

const ChooseRouteType = ({ onContinue }) => {
    const dispatch = useDispatch();

    const chosenRouteType = useSelector(selectStoredRouteType);
    const returnToSummaryAvailable = useSelector(selectReturnToSummaryAvailable);

    const handleChoiceChange = (e) => {
        dispatch(setStoredRouteType(e.target.value));
    };

    const handleReturnToSummary = () => {
        dispatch(setReturnToSummaryAvailable(false));
        dispatch(setStep(WIZARD_STEPS.SUMMARY));
    };

    return (
        //<Paper sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
        <Box>
            <Typography variant="h4" gutterBottom>
                Introduction and Choose Route Type
            </Typography>

            <Typography variant="body1" component="p" mb={3}>
                DumpsterRoute<sup>TM</sup> helps you figure out the best route for hauling dumpsters.
                You choose between two styles of dumping: “swap-and-drop" or “to-dump‑and‑back.”
                The app then has you select which dumpsters to haul and finally provides an optimized route.
            </Typography>

            <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                    Choose one style of dumping
                </FormLabel>

                <RadioGroup
                    name="dump-style"
                    value={chosenRouteType || ''}
                    onChange={handleChoiceChange}
                >
                    <FormControlLabel
                        value={ROUTE_TYPES.SWAP_AND_DROP}
                        control={<Radio color="primary" />}
                        label={
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    Swap-and-Drop
                                </Typography>
                                <Typography variant="body2">
                                    Start with an empty can, swap with a full one, haul, dump,
                                    and repeat until all “swappable” cans have been emptied.
                                    End by dropping the last empty at a drop‑lot.
                                </Typography>
                            </Box>
                        }
                    />

                    <FormControlLabel
                        sx={{ mt: 2 }}
                        value={ROUTE_TYPES.TO_DUMP_AND_BACK}
                        control={<Radio color="primary" />}
                        label={
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    To-Dump-and-Back
                                </Typography>
                                <Typography variant="body2">
                                    Go to a full can, haul it to a dump, return the empty
                                    to the same site, and repeat until all cans are emptied.
                                </Typography>
                            </Box>
                        }
                    />
                </RadioGroup>
            </FormControl>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <Button
                    variant="contained"
                    onClick={onContinue}
                    disabled={!chosenRouteType}
                >
                    Next
                </Button>

                {/* Render a "Return to Summary" button conditionally, basd on store */}
                { returnToSummaryAvailable && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleReturnToSummary}
                        sx={{ ml: 2 }}
                    >
                        Return to Summary
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default ChooseRouteType;
